import React, { useEffect, useMemo, useState } from 'react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';
import KanbanColumn from './KanbanColumn';
import { STAGES, ZONES } from '@/lib/constants';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import StageChangeModal from './StageChangeModal';
import ContactCard from './ContactCard';

export default function KanbanView({ contacts, agents, onChange }) {
    const { profile } = useAuth();
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
    const [activeId, setActiveId] = useState(null);
    const [stageModal, setStageModal] = useState(null); // { contact, newStage }
    const [filterZone, setFilterZone] = useState(null);

    const toggleZone = (key) => setFilterZone((z) => (z === key ? null : key));
    const visibleStages = filterZone ? (ZONES.find((z) => z.key === filterZone)?.stages ?? STAGES) : STAGES;

    const grouped = useMemo(() => {
        const m = Object.fromEntries(STAGES.map((s) => [s, []]));
        for (const c of contacts) (m[c.pipeline_stage] || (m[c.pipeline_stage] = [])).push(c);
        return m;
    }, [contacts]);

    const activeContact = activeId ? contacts.find((c) => c.id === activeId) : null;

    const onDragStart = (e) => setActiveId(e.active?.id || null);

    const onDragEnd = async (e) => {
        setActiveId(null);
        const id = e.active?.id;
        const newStage = e.over?.id;
        if (!id || !newStage) return;
        const c = contacts.find((x) => x.id === id);
        if (!c || c.pipeline_stage === newStage) return;

        if (newStage === 'Closed Won' || newStage === 'Closed Lost') {
            setStageModal({ contact: c, newStage });
            return;
        }
        await applyStageChange(c, newStage, {});
    };

    const applyStageChange = async (c, newStage, extra = {}) => {
        const update = { pipeline_stage: newStage, ...extra };
        if (newStage === 'Closed Won') update.is_client = true;
        const { error } = await supabase.from('pipeline_contacts').update(update).eq('id', c.id);
        if (error) { toast.error(`Move failed: ${error.message}`); return; }
        await supabase.from('pipeline_activities').insert({
            contact_id: c.id,
            organization_id: c.organization_id,
            agent_id: profile?.id,
            activity_type: 'status_change',
            description: `Stage changed: ${c.pipeline_stage} → ${newStage}`,
            old_stage: c.pipeline_stage,
            new_stage: newStage,
        });
        toast.success(`Moved to ${newStage}`);
        onChange?.();
    };

    return (
        <div className="px-4 lg:px-6 pb-4">
            <div className="flex items-center gap-2 px-1 py-2 mb-1 text-[11px] uppercase tracking-wider">
                {ZONES.map((z) => {
                    const active = filterZone === z.key;
                    return (
                        <button
                            key={z.key}
                            onClick={() => toggleZone(z.key)}
                            className={`flex items-center gap-2 h-7 px-2.5 rounded-md border transition ${active ? 'border-current' : 'border-transparent hover:bg-gray-100'}`}
                            style={active ? { background: `${z.color}14`, color: z.color } : {}}
                        >
                            <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ background: z.color }} />
                            <span className={`font-semibold ${active ? '' : 'text-gray-600'}`}>{z.label}</span>
                        </button>
                    );
                })}
            </div>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={onDragStart} onDragEnd={onDragEnd}>
                <div className="aks-kanban-track" data-testid="kanban-track">
                    {visibleStages.map((s) => (
                        <KanbanColumn key={s} stage={s} contacts={grouped[s] || []} agents={agents} />
                    ))}
                </div>
                <DragOverlay>{activeContact ? <div style={{ width: 300 }}><ContactCard contact={activeContact} agents={agents} /></div> : null}</DragOverlay>
            </DndContext>
            {stageModal && (
                <StageChangeModal
                    contact={stageModal.contact}
                    newStage={stageModal.newStage}
                    onClose={() => setStageModal(null)}
                    onConfirm={(extra) => { applyStageChange(stageModal.contact, stageModal.newStage, extra); setStageModal(null); }}
                />
            )}
        </div>
    );
}
